import { Fragment } from 'react';
import {
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  IconButton,
  Box,
} from '@chakra-ui/react';
import type { ChangeEvent } from 'react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';
import { useAppSelector, useAppDispatch } from 'hooks';
import {
  setCoApplicantField,
  addCoApplicant,
  removeCoApplicant,
} from 'reducers/commercialFormReducer';

interface CommercialCoApplicantsProps {
  isLocked?: boolean;
}

const MAX_CO_APPLICANTS = 3;

const CommercialCoApplicants = ({
  isLocked = false,
}: CommercialCoApplicantsProps) => {
  const dispatch = useAppDispatch();
  const coApplicants = useAppSelector(
    (state) =>
      state.commercialForm.data.co_applicants || [{ name: '', email: '' }],
  );

  const handleChange =
    (index: number, field: 'name' | 'email') =>
    (event: ChangeEvent<HTMLInputElement>) => {
      dispatch(
        setCoApplicantField({ index, field, value: event.target.value }),
      );
    };

  return (
    <Card mb="20px" id="sectionCommercialCoApplicants">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Co-Applicants
      </Text>

      <VStack spacing={4} align="stretch">
        {coApplicants.map((entry, index) => {
          const nameRequired = index > 0 || !!entry.email;
          const emailRequired = index > 0 || !!entry.name;
          const nameMissing = nameRequired && !entry.name;
          const emailMissing = emailRequired && !entry.email;

          return (
            <Fragment key={index}>
              {index === 1 && (
                <Text fontWeight="semibold" fontSize="md" mt={2}>
                  Additional Co-Applicants
                </Text>
              )}
              <Box>
                <HStack spacing={4} align="flex-end">
                  <FormControl
                    flex={1}
                    isRequired={nameRequired}
                    isInvalid={nameMissing}
                  >
                    <FormLabel ml="0">Name</FormLabel>
                    <Input
                      variant="main"
                      value={entry.name}
                      onChange={handleChange(index, 'name')}
                      placeholder="Enter name"
                      isDisabled={isLocked}
                      borderColor={nameMissing ? 'red' : 'inherit'}
                    />
                  </FormControl>

                  <FormControl
                    flex={1}
                    isRequired={emailRequired}
                    isInvalid={emailMissing}
                  >
                    <FormLabel ml="0">Email</FormLabel>
                    <Input
                      variant="main"
                      type="email"
                      value={entry.email}
                      onChange={handleChange(index, 'email')}
                      placeholder="Enter email"
                      isDisabled={isLocked}
                      borderColor={emailMissing ? 'red' : 'inherit'}
                    />
                  </FormControl>

                  {index > 0 ? (
                    <IconButton
                      aria-label="Remove co-applicant"
                      icon={<DeleteIcon />}
                      onClick={() => dispatch(removeCoApplicant(index))}
                      isDisabled={isLocked}
                      variant="ghost"
                    />
                  ) : (
                    <Box w="40px" flexShrink={0} />
                  )}
                </HStack>
                {(nameMissing || emailMissing) && (
                  <HStack spacing={4} align="flex-start" mt={1}>
                    <Box flex={1}>
                      {nameMissing && (
                        <Text color="red.500" fontSize="sm">
                          Name is required
                        </Text>
                      )}
                    </Box>
                    <Box flex={1}>
                      {emailMissing && (
                        <Text color="red.500" fontSize="sm">
                          Email is required
                        </Text>
                      )}
                    </Box>
                    <Box w="40px" flexShrink={0} />
                  </HStack>
                )}
              </Box>
            </Fragment>
          );
        })}

        <Box>
          <Button
            leftIcon={<AddIcon />}
            onClick={() => dispatch(addCoApplicant())}
            isDisabled={
              isLocked || coApplicants.length >= MAX_CO_APPLICANTS
            }
            variant="outline"
            size="sm"
          >
            Add Co-Applicant
          </Button>
        </Box>
      </VStack>
    </Card>
  );
};

export default CommercialCoApplicants;
